"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import { SwiperSlide } from "swiper/react";
import { useSearchParams, usePathname } from "next/navigation";
import { useMemo } from "react";
import { useLocale } from "next-intl";
import { useLoading } from "@/contexts/LoadingContext";
import Swiper from "@/components/swiper";
import { Link } from "@/i18n/navigation";
import { stripLeadingLocalePrefix } from "@/utils/i18n/stripLeadingLocalePrefix";
import { useCurrency } from "@/contexts/CurrencyContext";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import { useHotelFavorite } from "@/hooks/useHotelFavorite";
import { buildFromSearchData } from "@/utils/hotel/buildFavoritedHotelPayload";
import { useHotelAddToWishlistAnalytics } from "@/components/pages/hotel/search/hooks/useHotelAddToWishlistAnalytics";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";

const amenityIconMap = {
  3: "circle-wifi",
  7: "pet-custom",
  8: "wheelchair-1",
  9: "circle-parking",
  11: "vault",
  13: "water-ladder-1-1",
  22: "child-hotel",
  23: "air-conditioner",
  27: "bus-service-1",
  28: "umbrella-beach-1",
  30: "hot-tub-person",
  39: "water-ladder-1-1",
  60: "water-ladder-1-1",
  85: "unlock-room",
  88: "cstm-smarttv",
  89: "custom-nespresso",
  107: "hotel-building-new",
  159: "person-swimming-child-1",
  186: "charging-station-1",
  247: "water",
  257: "spa-1",
  267: "shower",
  268: "shower",
  269: "bath",
  270: "hair-dryer",
  272: "mini-bar",
  292: "terrace-new",
  295: "leak-sea",
  296: "leak-sea",
  297: "tree",
  302: "airplane-otel",
  372: "mountbig",
  373: "blueflag",
  404: "tulip",
  563: "umbrella-beach-1",
  720: "circle-parking",
  721: "circle-parking",
  727: "circle-parking",
};

/**
 * Harita modalındaki otel kartı. SearchCardItem yapısının harita paneli için kopyası;
 * dar container’a göre kompakt layout. Search sayfasını etkilemez.
 */
export default function MapCardItem({
  hotelData,
  onSelect,
  onClose,
  itemListId,
  itemListName,
  passengers,
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { show } = useLoading();
  const locale = useLocale();
  const { currency } = useCurrency();
  const { formatPrice } = usePriceFormatter();

  const { trackAddToWishlist } = useHotelAddToWishlistAnalytics({
    product: hotelData,
    itemListId,
    itemListName,
    passengers,
    currency: currency ?? undefined,
    value: hotelData?.price ?? undefined,
  });

  const favoriteContext = useMemo(
    () => ({
      pathname: pathname ?? "",
      searchParams: searchParams ?? {},
      locale: locale ?? "tr",
      currency: currency ?? "TRY",
    }),
    [pathname, searchParams, locale, currency],
  );
  const { isFavorite, isAddingFavorite, handleFavoriteClick } =
    useHotelFavorite({
      hotelData,
      getPayload: buildFromSearchData,
      context: favoriteContext,
    });

  const isSearchPage = useMemo(() => {
    return (
      pathname?.includes("/hotel/search/") || pathname?.includes("/otel/ara/")
    );
  }, [pathname]);
  const isCampaignApplied = hotelData?.IsCampaignApplied === true;

  const queryString = useMemo(() => {
    if (!isSearchPage) return "";

    const params = new URLSearchParams();
    const pathParts = pathname.split("/").filter(Boolean);
    const searchIndex = pathParts.findIndex(
      (part) => part === "search" || part === "ara",
    );

    if (searchIndex !== -1) {
      const checkinDate = pathParts[searchIndex + 2];
      const checkoutDate = pathParts[searchIndex + 3];
      if (checkinDate) params.set("check_in", checkinDate);
      if (checkoutDate) params.set("check_out", checkoutDate);
    }

    const adult =
      searchParams.get("adult") || searchParams.get("adult_1") || "1";
    const child =
      searchParams.get("child") || searchParams.get("child_1") || "0";
    params.set("adult_1", adult);

    if (child && child !== "0") {
      params.set("child_1", child);
      const childCount = parseInt(child, 10);
      const childAges = searchParams.get("childAges");
      if (childAges) {
        const decodedAges = decodeURIComponent(childAges);
        const ages = decodedAges
          .split(",")
          .filter(Boolean)
          .map((age) => age.trim());
        ages.forEach((age, index) => {
          if (index < childCount) params.set(`childage_1_${index + 1}`, age);
        });
      } else {
        for (let i = 1; i <= childCount; i++) {
          const childAge = searchParams.get(`childage_1_${i}`);
          if (childAge) params.set(`childage_1_${i}`, childAge);
        }
      }
    }

    if (isCampaignApplied) {
      const rawCampaign = searchParams.get("campaignId");
      if (
        rawCampaign !== null &&
        rawCampaign !== undefined &&
        String(rawCampaign).trim() !== ""
      ) {
        params.set("campaignId", String(rawCampaign).trim());
      }
    }

    return params.toString();
  }, [isSearchPage, pathname, searchParams, isCampaignApplied]);

  const linkUrl = useMemo(() => {
    const baseLink = stripLeadingLocalePrefix(hotelData.link, locale);
    if (!isSearchPage || !queryString) return baseLink;
    const separator = baseLink.includes("?") ? "&" : "?";
    return `${baseLink}${separator}${queryString}`;
  }, [hotelData.link, isSearchPage, queryString, locale]);

  const handleLinkClick = () => {
    if (typeof onSelect === "function") onSelect();
    show();
  };

  return (
    <Link
      href={linkUrl}
      className="hotel-card-item map-card-item relative block w-full overflow-hidden"
      onClick={handleLinkClick}
    >
      {/* Mobil harita için kapatma butonu (desktop'ta gösterilmez) */}
      {typeof onClose === "function" && (
        <button
          type="button"
          aria-label="Kapat"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="text-foreground-muted absolute top-0 right-0 z-20 flex items-center justify-center lg:hidden"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.far["xmark"]}
            className="h-5! w-5!"
          />
        </button>
      )}

      <div className="flex flex-row items-stretch gap-3 lg:flex-col">
        <div className="relative w-[120px] flex-shrink-0 sm:w-[180px] md:w-[220px] lg:aspect-[16/10] lg:h-auto lg:max-h-[140px] lg:w-full">
          <div
            className="swiper-responsive-container relative h-full min-h-[150px] w-full overflow-hidden rounded-lg bg-gray-200"
            onClick={(e) => {
              const target = e.target;
              const isNavigationButton =
                target.closest(".swiper-button-next") ||
                target.closest(".swiper-button-prev") ||
                target.closest(".swiper-navigations") ||
                target.classList.contains("swiper-button-next") ||
                target.classList.contains("swiper-button-prev");
              const isPaginationBullet =
                target.closest(".swiper-pagination") ||
                target.classList.contains("swiper-pagination-bullet") ||
                target.closest(".swiper-pagination-bullet");
              if (isNavigationButton || isPaginationBullet) e.stopPropagation();
            }}
          >
            <Swiper
              className="h-full w-full"
              customClass="mobile-hotel-gallery-swiper"
              swiperOptions={{
                loop: true,
                navigation: true,
                navigationPrevButtonCustomClass:
                  "hidden xl:flex absolute top-[50%] text-base! text-foreground-muted left-4 z-10 m-0 h-[30px]! w-[30px]! -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 p-1 shadow-[0_4px_12px_2px_rgba(20,20,20,0.06)] transition-all duration-300 hover:bg-white",
                navigationNextButtonCustomClass:
                  "hidden xl:flex absolute top-[50%] text-base! text-foreground-muted right-4 z-10 m-0 h-[30px]! w-[30px]! -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 p-1 shadow-[0_4px_12px_2px_rgba(20,20,20,0.06)] transition-all duration-300 hover:bg-white",
                navigationPrevButtonIcon: (
                  <FontAwesomeIcon
                    className="h-[16px]! w-[16px]!"
                    icon={byPrefixAndName.far["angle-left"]}
                  />
                ),
                navigationNextButtonIcon: (
                  <FontAwesomeIcon
                    className="h-[16px]! w-[16px]!"
                    icon={byPrefixAndName.far["angle-right"]}
                  />
                ),
                pagination: {
                  dynamicBullets: true,
                  dynamicMainBullets: 2,
                  hideOnClick: false,
                  bulletClass: "room-card-bullet",
                  bulletActiveClass: "room-card-bullet-active",
                  containerClass:
                    "absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2",
                },
                mousewheel: true,
                keyboard: true,
                slidesPerView: 1,
                spaceBetween: 0,
              }}
            >
              {hotelData.imageUrl.map((image, index) => (
                <SwiperSlide key={index} className="h-full w-full bg-gray-50">
                  <Image
                    src={image || "/images/dummy/hotel-placeholder.jpg"}
                    width={280}
                    height={184}
                    alt={hotelData.name}
                    className="h-full w-full object-cover"
                    unoptimized={true}
                    loading={index === 0 ? "eager" : "lazy"}
                    priority={index === 0}
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 182px, 280px"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Favori ikonu - SearchCardItem ile aynı davranış */}
            <button
              onClick={(e) => {
                if (!isFavorite) trackAddToWishlist();
                handleFavoriteClick(e);
              }}
              disabled={isAddingFavorite}
              className="absolute top-3 right-3 z-10 h-7 w-7 cursor-pointer transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isFavorite ? (
                <Image
                  src={getCdnImagePath("/saka/hotel/like-hotel-fill.svg")}
                  alt="like-card"
                  width={28}
                  height={28}
                />
              ) : (
                <Image
                  src={getCdnImagePath("/saka/hotel/like-hotel.svg")}
                  alt="like-card"
                  width={28}
                  height={28}
                />
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-1 flex-col">
          <div className="flex flex-col gap-2 lg:gap-[2px]">
            <h3 className="text-foreground flex flex-wrap items-center gap-[2px] gap-x-[6px] pr-3 text-base font-semibold lg:text-lg">
              {hotelData.name}
              <span className="flex items-center gap-[1px]">
                {[...Array(hotelData.starRating)].map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={byPrefixAndName.fas["star"]}
                    className="text-yellow !h-[14px] !w-[14px]"
                  />
                ))}
              </span>
            </h3>
            <p className="text-foreground text-sm">{hotelData.location}</p>

            {/* Rating + kahvaltı bilgisi: mobil & tablet için başlık altında (lg altında) */}
            <div className="flex flex-col gap-[6px] lg:hidden">
              {hotelData.rating !== null &&
                hotelData.rating !== undefined &&
                typeof hotelData.rating === "number" &&
                hotelData.rating > 0 &&
                hotelData.ratingText && (
                  <div className="flex items-center gap-[6px]">
                    <div className="bg-foreground w-[26px] rounded-sm px-[5px] py-[2px] text-center text-xs font-medium text-white">
                      {hotelData.rating}
                    </div>
                    <span className="text-foreground text-sm font-medium">
                      {hotelData.ratingText}
                    </span>
                  </div>
                )}

              {hotelData.breakfast && (
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon
                    className="text-teal h-[18px]! w-[18px]!"
                    icon={byPrefixAndName.fak["custom-checkmark-fat-1"]}
                  />
                  <span className="text-teal text-sm">
                    {hotelData.breakfast}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex lg:mt-2">
            <div className="hidden flex-1 flex-col gap-[10px] lg:flex">
              {/* Rating + kahvaltı bilgisi: sadece lg ve üstü için alttaki blokta */}
              <div className="hidden flex-col gap-[10px] lg:flex">
                {hotelData.rating !== null &&
                  hotelData.rating !== undefined &&
                  typeof hotelData.rating === "number" &&
                  hotelData.rating > 0 &&
                  hotelData.ratingText && (
                    <div className="flex items-center gap-[6px]">
                      <div className="bg-foreground w-[26px] rounded-sm px-[5px] py-[2px] text-center text-xs font-medium text-white">
                        {hotelData.rating}
                      </div>
                      <span className="text-foreground text-sm font-medium">
                        {hotelData.ratingText}
                      </span>
                    </div>
                  )}

                {hotelData.breakfast && (
                  <div className="flex items-center gap-1">
                    <FontAwesomeIcon
                      className="text-teal h-[18px]! w-[18px]!"
                      icon={byPrefixAndName.fak["custom-checkmark-fat-1"]}
                    />
                    <span className="text-teal text-sm">
                      {hotelData.breakfast}
                    </span>
                  </div>
                )}
              </div>

              {hotelData.amenities?.length > 0 && (
                <div className="hidden flex-wrap items-center gap-x-4 gap-y-2 lg:flex">
                  {hotelData.amenities.slice(0, 3).map((amenity, index) => {
                    const iconName = amenityIconMap[amenity.Code] || "home";
                    const icon =
                      byPrefixAndName.fak[iconName] ||
                      byPrefixAndName.fal[iconName] ||
                      byPrefixAndName.fal["home"];
                    return (
                      <div key={index} className="flex items-center gap-[6px]">
                        <FontAwesomeIcon
                          icon={icon}
                          className="text-foreground h-[18px]! w-[18px]! text-sm"
                        />
                        <span className="text-foreground text-sm">
                          {amenity.Name || amenity}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {hotelData.price && (
              <div className="mt-1 flex flex-1 flex-col items-end justify-end gap-4">
                <div className="w-full text-right">
                  {hotelData.discountRate && (
                    <div className="mb-[4px] flex justify-end">
                      <div className="bg-promo-weak text-promo rounded-sm px-[6px] py-[3px] text-sm font-medium">
                        %{hotelData.discountRate} indirim
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col items-end justify-end gap-[2px]">
                    {hotelData.priceOriginal && (
                      <div className="text-foreground-muted text-sm line-through">
                        {formatPrice(hotelData.priceOriginal, currency, 0)}
                      </div>
                    )}
                    <div className="text-foreground text-[18px] font-semibold lg:text-[23px]">
                      {formatPrice(hotelData.price, currency, 0)}
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <div className="text-foreground-muted mt-[2px] text-sm">
                        {[hotelData.duration, hotelData.guestSummary]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
